/**
 * Silences the benign "ResizeObserver loop ..." error.
 *
 * `@formily/grid` (used by every FormGrid, and our permit forms are full of them)
 * bundles the `@juggle/resize-observer` ponyfill. When a resize callback triggers
 * another layout in the same pass it emulates the native browser warning by doing
 * `window.dispatchEvent(new ErrorEvent('error', { message: 'ResizeObserver loop
 * completed with undelivered notifications.' }))`. It is harmless, but the CRA dev
 * error overlay surfaces it full-screen.
 *
 * A plain `addEventListener('error', …)` can't stop it: for an event dispatched
 * directly on `window`, listeners fire in registration order (capture is ignored
 * at the target), and CRA's overlay registers its listener before our app code
 * runs. So we intercept `window.dispatchEvent` itself and drop just this one
 * event before any listener sees it. Everything else passes through untouched.
 */
const MESSAGE =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

if (typeof window !== "undefined") {
  const isBenign = (e: unknown): boolean =>
    !!e &&
    typeof (e as ErrorEvent).message === "string" &&
    MESSAGE.test((e as ErrorEvent).message);

  // Primary fix: swallow the synthetic ErrorEvent at the dispatch source.
  const originalDispatch = window.dispatchEvent.bind(window);
  window.dispatchEvent = (event: Event): boolean => {
    if (event && event.type === "error" && isBenign(event)) return true;
    return originalDispatch(event);
  };

  // Belt-and-suspenders for any path that reports it differently.
  window.addEventListener(
    "error",
    (event) => {
      if (isBenign(event)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === "string" && MESSAGE.test(args[0])) return;
    originalConsoleError(...args);
  };
}

/**
 * Root cause, not just the symptom: defer every native ResizeObserver callback
 * to the next animation frame.
 *
 * The suppression above only ever caught the *synthetic* event `@juggle`
 * dispatches. antd's tables observe through `rc-resize-observer` ->
 * `resize-observer-polyfill`, which hands back the browser's own
 * `ResizeObserver` whenever there is one -- and when a callback of that
 * observer changes layout, the browser reports "loop completed with undelivered
 * notifications" itself. That error is dispatched by the browser onto `window`,
 * so the `dispatchEvent` patch never sees it, and the dev-server overlay's own
 * listener is registered by an entry that runs before ours (at the event target
 * capture does not jump the queue) -- which is why every editable table still
 * flashed the overlay.
 *
 * Running the callback one frame later takes it out of the observation delivery
 * loop entirely: whatever it resizes is picked up by the next round instead of
 * being an undelivered notification of this one. The measurement lands a frame
 * late, which is invisible, and a backgrounded tab (where rAF is paused) is not
 * resizing anything -- it catches up when it is shown again.
 *
 * This must run before anything imports antd/formily: `resize-observer-polyfill`
 * reads `window.ResizeObserver` at module-evaluation time, so the patch has to
 * already be in place. The entry point imports this file first for that reason.
 */
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const Native = window.ResizeObserver;

  // A frame *or* a macrotask, whichever comes first. rAF alone is the usual
  // recipe, but it is paused in a backgrounded tab -- which would leave every
  // pending measurement stalled until the tab is looked at again. A timeout
  // alone is throttled in that same tab. Racing them means the callback always
  // runs at the next opportunity the browser actually has, and either way it
  // has left the observation delivery loop, which is the whole point.
  const defer = (run: () => void) => {
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      run();
    };
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(once);
    }
    window.setTimeout(once, 0);
  };

  class DeferredResizeObserver extends Native {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        defer(() => {
          // The callback can outlive its observer (a table unmounts between the
          // resize and the frame). Report rather than swallow: anything thrown
          // here would otherwise surface as an error a frame from nowhere.
          try {
            callback(entries, observer);
          } catch (error) {
            console.error(error);
          }
        });
      });
    }
  }

  window.ResizeObserver = DeferredResizeObserver;
}

export {};
